<template>
  <div id="app">
    <div class="catalog-container">
      <!-- Unpublished -->
      <b-alert
        :show="!catalogueActive"
        variant="secondary"
        class="unpublished-container"
      >
        This catalogue is currently unpublished and not visible to users.
      </b-alert>
      <!-- Filter Panel -->
      <div
        v-if="displayFilters"
        class="filter-container"
      >
        <noscript>Filtering options are only available if javascript is enabled.</noscript>
        <no-ssr>
          <div
            :class="{ 'expanded' : isActive}"
            class="filter-header"
            @click="isActive = !isActive "
          >
            <h3>
              <span v-if="!isActive">Show</span>
              <span v-if="isActive">Hide</span> Filters
            </h3>
            <catalog-svg-icons name="ChevronIcon" />
          </div>
        </no-ssr>

        <no-ssr>
          <div
            :class="{ 'showing-mobile-filters' : isActive}"
            class="filter-region-content"
          >
            <div class="filter-region-title">
              <h3>Refine by</h3>
            </div>
            <filter-panel-view
              v-model="filterValues"
              :filters="filters"
              :products="products"
            />
          </div>
        </no-ssr>
      </div>

      <div
        class="main-container"
      >
        <div class="sorting-container">
          <b-row>
            <b-col>
              <noscript>Sorting options are only available if javascript is enabled.</noscript>
              <no-ssr>
                <div class="top-filters">
                  <!-- Sorting Options -->
                  <sorting
                    v-model="sorting"
                    :sorters="sorters"
                  />
                  <!-- View Type Toggle -->
                  <view-toggle v-model="view" />
                </div>
              </no-ssr>
            </b-col>
          </b-row>
        </div>
        <div class="product-container">
          <!-- List Catalog Items -->
          <component
            :is="view"
            :items="catalogueData"
            :class="cardTheme"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CatalogSvgIcons from './components/ui-elements/catalog-svg-icons'
import ViewToggle from './components/toggles/view-toggle'
import Sorting from './components/filters/generic-sorting'
import ListView from './views/list-view'
import GridView from './views/grid-view'
import FilterPanelView from './views/filter-panel-view'
import assign from 'lodash/assign'

export default {
  name: 'route-catalog',
  components: {
    CatalogSvgIcons,
    'view-toggle': ViewToggle,
    'list-view': ListView,
    'grid-view': GridView,
    'filter-panel-view': FilterPanelView,
    'sorting': Sorting
  },
  props: {
    'value': {
      type: Object,
      required: true
    },
    'filters': {
      type: Array,
      required: true
    },
    'sorters': {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isActive: false
    }
  },
  computed: {
    catalogueActive() {
      return this.$store.getters['catalogues/isActive'](this.catalogueID)
    },
    displayFilters() {
      return this.$route.meta.showCatalogueFilters ?? true
    },
    catalogueID: function() {
      return this.$route.meta.catalogueID
    },
    products: function() {
      const catalogueItems = this.$store.getters['catalogues/getSingleCatalogue'](this.catalogueID)
      if (!catalogueItems || !Object.prototype.hasOwnProperty.call(catalogueItems, 'products') || !catalogueItems.products.length) return []
      return catalogueItems.products.map(id => this.$store.getters['products/findProductByID'](id))
        .filter(_ => _)
    },
    view: {
      get: function() {
        return this.value.view
      },
      set: function(value) {
        let newValue = JSON.parse(JSON.stringify(this.value))
        newValue.view = value
        this.$emit('input', newValue)
      }
    },
    themeSettings() {
      return this.$store.state.registry.getModuleSetting('theme') || {}
    },
    cardTheme() {
      return this.retrieveConcilioLayoutSetting('theme')
    },
    concilioSettings() {
      return Object.keys(this.themeSettings).includes('concilio-settings') ? this.themeSettings['concilio-settings'] : null
    },
    themeProductSettings() {
      return Object.keys(this.themeSettings).includes('products') ? this.themeSettings.products : null
    },
    themeConcilioSettings() {
      return Object.keys(this.themeSettings).includes('concilio-settings') ? this.themeSettings.concilioSettings : null
    },
    carouselItemSettings() {
      return this.themeSettings !== null ? this.themeSettings.sliders : null
    },
    hoverCardSetting() {
      return this.retrieveConcilioLayoutSetting('hoverDisplay')
    },
    carouselItemLayout() {
      const viewKey = (this.view === 'grid-view') ? 'grid' : 'list'
      const registryKey = `catalogue-${viewKey}`
      const themeSettingsActive = (this.themeProductSettings !== null && Object.keys(this.themeProductSettings).includes(registryKey))

      return (themeSettingsActive) ? `${this.themeProductSettings[registryKey]}` || 'wine-product-grid-item-0001' : 'wine-product-grid-item-0001'
    },
    catalogueData: function() {
      return this.items
    },
    sorting: {
      get: function() {
        let sorters = this.value.sorters
        let sortDir = this.value.sortDir
        return Object.assign({}, { sorters, sortDir })
      },
      set: function(value) {
        let newValue = JSON.parse(JSON.stringify(this.value))
        newValue.sorters = value.sorters
        newValue.sortDir = value.sortDir
        this.$emit('input', newValue)
      }
    },
    filterValues: {
      get: function() {
        return this.value.filters
      },
      set: function(value) {
        let newValue = JSON.parse(JSON.stringify(this.value))
        newValue.filters = value
        this.$emit('input', newValue)
      }
    },
    items() {
      const includesAValue = (srcArray, filtersArray) => {
        let intersection = []
        if (Array.isArray(srcArray)) {
          intersection = srcArray.filter(v => filtersArray.includes(v))
        }
        else {
          intersection = filtersArray.filter(v => v === srcArray)
        }
        return intersection.length !== 0
      }
      let searchValues = assign({}, this.filterValues)
      let items = this.products.filter(item => {
        if (this.$store.getters['user/userid'] !== '1' && item.catalogvisibility && item.catalogvisibility.length !== 0 && !this.$store.getters['user/hasAnyRole'](item.catalogvisibility.map(data => data.role))) return false
        return this.filters.reduce((show, filter) => {
          let fv = searchValues[filter.filterBy]
          switch (filter.type) {
          case 'range-slider':
            if (item[filter.filterBy] < fv[0] || item[filter.filterBy] > fv[1]) {
              return false
            }
            return show

          default:
            if (Array.isArray(fv)) {
              if (fv.length) {
                let f = includesAValue(item[filter.filterBy], fv)
                return f && show
              }
              else {
                return show
              }
            }
            else {
              return show && (item[filter.filterBy] === searchValues[filter.filterBy])
            }
          }
        }, true)
      })

      // Sort items if sorting has been attached
      let sortBy = this.sorting.sorters
      let sortDir = this.sorting.sortDir
      if (sortBy) {
        let filter = this.sorters.filter(x => x.sortBy === sortBy).shift()
        let numeric = (filter && filter.hasOwnProperty('numeric')) ? filter.numeric : false
        const store = this.$store
        items = items.sort(function(a, b) {
          if ((a[sortBy] === null || b[sortBy] === null) || (typeof a[sortBy] !== typeof b[sortBy])) {
            return null
          }

          switch (sortBy) {
          case 'sellPrice':
            a[sortBy] = store.getters['products/sellPrice'](a)
            b[sortBy] = store.getters['products/sellPrice'](b)
            break
          case 'basePrice':
            a[sortBy] = store.getters['products/basePrice'](a)
            b[sortBy] = store.getters['products/basePrice'](b)
            break
          case 'unitPrice':
            a[sortBy] = store.getters['products/unitPrice'](a)
            b[sortBy] = store.getters['products/unitPrice'](b)
            break
          case 'rrpPrice':
            a[sortBy] = store.getters['products/rrpPrice'](a)
            b[sortBy] = store.getters['products/rrpPrice'](b)
            break
          }

          if (filter.type === 'taxonomy') {
            const taxonomy = store.getters['productCatalog/getTaxonomy'](sortBy)
            const aOrder = taxonomy.findIndex(term => term.description === a[sortBy])
            const bOrder = taxonomy.findIndex(term => term.description === b[sortBy])
            if (aOrder === bOrder && a[filter.fallback] && b[filter.fallback]) {
              return a[filter.fallback].localeCompare(b[filter.fallback], 'co', { numeric: false, usage: 'sort', sensitivity: 'base' })
            }
            return aOrder - bOrder
          }

          if (typeof a[sortBy] === 'string') {
            return (a[sortBy]).localeCompare(b[sortBy], 'co', { numeric: numeric, usage: 'sort', sensitivity: 'base' })
          }
          else if (typeof a[sortBy] === 'undefined') {
            return -1
          }
          else if (typeof b[sortBy] === 'undefined') {
            return 1
          }
          else {
            if (a[sortBy] < b[sortBy]) return -1
            if (a[sortBy] > b[sortBy]) return 1
            return 0
          }
        })
      }

      if (sortDir === 'DESC') {
        items = items.reverse()
      }
      return items
    }
  },
  created: function() {
    const queryStr = this.$route.query
    if (this.$route.query !== undefined) {
      const newValues = this.filters.reduce(function(vals, filter) {
        if (queryStr[ filter.filterBy ] !== undefined) {
          vals.filters[filter.filterBy] = queryStr[ filter.filterBy ].constructor === Array ? queryStr[ filter.filterBy ] : [ queryStr[ filter.filterBy ] ]
        }
        else if (queryStr[ `${filter.filterBy}[]` ] !== undefined) {
          vals.filters[filter.filterBy] = queryStr[`${filter.filterBy}[]`].constructor === Array ? queryStr[`${filter.filterBy}[]`] : [ queryStr[`${filter.filterBy}[]`] ]
        }
        return vals
      }, JSON.parse(JSON.stringify(this.value)))
      this.$emit('input', newValues)
    }
  },
  mounted: function() {
    // this.$store.dispatch('productCatalog/loadProductStock')
    this.$store.dispatch('products/currentStock')
  },
  methods: {
    filterBy(filter, products) {
      let productFilter = this.catalog.settings.filters[filter]

      if (productFilter.length) {
        if (filter !== 'sweetness') {
          products = products.filter(product => {
            return productFilter.some((arg) => product[filter].includes(arg))
          })
        }
        else {
          products = products.filter(product => {
            return (product[filter] >= productFilter[0] && product[filter] <= productFilter[1])
          })
        }
      }
      return products
    },
    retrieveConcilioLayoutSetting(payload) {
      const concilioSettingsExist = this.concilioSettings !== null
      const currentLayout = this.carouselItemLayout
      const layoutSettingsExist = (concilioSettingsExist) ? Object.keys(this.concilioSettings).includes(currentLayout) : false

      return (concilioSettingsExist) ? layoutSettingsExist ? this.concilioSettings[currentLayout][payload] : null : null
    }
  }
}
</script>
<style lang="scss">
@import '~@scss/custom.scss';
.unpublished-container{
  width: 100%;
  text-align: center;
}

.filter-region-title {
  @include media-breakpoint-down(md) {
    display: none;
  }
}
.filter-region-content {
  @include media-breakpoint-down(md) {
    height:0;
    overflow:hidden;
    &.showing-mobile-filters {
      height:auto;

    }
  }
}
.filter-container {
  @include media-breakpoint-down(md) {
    border:1px solid;
    border-color:var(--main-border-color);
    padding:1em;
  }
}
.filter-header {
  cursor: pointer;
  display:flex;
  justify-content: center;
  align-items: center;
  h3 {
    margin:0;
  }
  &.expanded {
    .icon {
      transform-origin: center;
      transform: rotate(180deg);
    }
  }
  .icon-container {
    margin-left:auto;
    .icon {
      @include transition(transform 0.3s);
      width:2em;
      height:2em;
    }
  }
  @include media-breakpoint-up(lg) {
    display:none;
  }

}
#app {
  ul {
    list-style: none;
  }

  .catalog-container {
    /* Form elements */
    .form-control {
      border: none;
      height: 2.5em;
      line-height: 2.5em;
      border-radius: 0;
    }

    .filter-header {
      width: 100%;
    }

    display: flex;
    padding:3em 0em;
    flex-wrap:wrap;
    .filter-container {
      //flex: 1;
      //min-width: 16em;
      margin-bottom:2em;
      width:100%;
      @include media-breakpoint-up(lg) {
        margin:0em;
        max-width:25%;
        width:25%;
      }
      .category-filter {
        border-bottom:1px solid;
        border-color:var(--main-border-color);
        .range-slider{
          padding:0em;
        }
      }
    }

    .main-container {
      flex-wrap: nowrap;
      flex: 3;
      @include media-breakpoint-up(lg) {
        margin-left: 3em;
        width: calc(75% - 3em);
      }
      .sorting-container {
        width: 100%;
        margin-bottom: 1.5em;
        border-bottom: 1px solid var(--main-border-color);
        padding-bottom: 1em;
      }
    }
  }

  /* Defining Transitions */
  /* Slide to right */
  .slide-right-leave-active,
  .slide-right-enter-active {
    transition: all .5s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }
  .slide-right-enter {
    transform: translate(-100%, 0);
  }
  .slide-right-leave-to {
    transform: translate(-100%, 0);
  }

  /* Slide to left */
  .slide-leave-active,
  .slide-enter-active {
    transition: all .5s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }
  .slide-enter {
    transform: translate(100%, 0);
  }
  .slide-leave-to {
    transform: translate(100%, 0);
  }

  /* Fade Transition */

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
  {
    opacity: 0;
  }
}

.top-filters {
  display: flex;
  width:100%;
  .category-filter {
    min-width:50%;
    ul {
      margin:0;
    }
  }
  .view-toggle {
    margin-left:auto;
  }
}

.pack-image {
  height: 280px;
  width: 100%;
  margin: 0em auto;
  justify-content: center;
  display: flex;
  align-items: center;
  padding: 1em;
  background: #efefef;

  img {
    max-height: 100%;
    width: auto;
  }
}

</style>
